import { FIELD_UPDATE_SOURCE } from "@common/constants";
import { formatISO } from "date-fns";
import { uuidv7 } from "uuidv7";
import { computeAccrualLineAmount } from "@common/helpers/accrual-line-item-amount.helper";
import { roundToCents } from "@common/helpers/monetary";
import { InvoiceExtractionLineItem } from "@common/schemas/financialDocument/invoice-extraction-line-item.schema";
import { InvoiceExtractionRecord } from "@common/schemas/financialDocument/invoice-extraction-record.schema";
import {
    NativeInvoiceCustomer,
    NativeInvoiceFormRawState,
    NativeInvoiceLineRow,
} from "@components/invoices/NativeInvoiceDrawer/nativeInvoiceFormTypes";
import { multiplyNativeInvoiceLineAmount } from "@components/invoices/NativeInvoiceDrawer/computeNativeInvoiceTotals";

export interface AccrualDraftInvoiceFormSlice {
    customer: NativeInvoiceCustomer | null;
    invoiceNumber: string;
    invoiceDate: string;
    dueDate: string;
    note: string;
    lines: NativeInvoiceLineRow[];
}

function emptyToNull(value: string): string | null {
    if (value === "") {
        return null;
    }
    return value;
}

function customerFromExtraction(
    extraction: InvoiceExtractionRecord,
    customer: NativeInvoiceCustomer | null,
): NativeInvoiceCustomer | null {
    if (customer) {
        return customer;
    }
    if (!extraction.counterpartyId) {
        return null;
    }
    return {
        id: extraction.counterpartyId,
        name: extraction.counterpartyName ?? "",
        email: null,
        phoneNumber: null,
    };
}

function quantityFromExtraction(line: InvoiceExtractionLineItem): number {
    if (line.unitPrice != null && line.quantity != null) {
        return line.quantity;
    }
    return 1;
}

function unitAmountFromExtraction(line: InvoiceExtractionLineItem): number {
    if (line.unitPrice != null) {
        return line.unitPrice;
    }
    return line.amount;
}

function lineFromExtraction(
    line: InvoiceExtractionLineItem,
): NativeInvoiceLineRow {
    return {
        id: line.lineId,
        catalogItemId: line.catalogItemId ?? null,
        catalogItemPriceId: line.catalogItemPriceId ?? null,
        productOrService: line.item ?? "",
        description: line.description ?? "",
        quantity: quantityFromExtraction(line),
        unitAmount: unitAmountFromExtraction(line),
        classIds: line.classIds ?? [],
        accrualAccountId: line.accountId,
        receivablesAccountId: line.accrualAccountId,
        tax: 0,
        discount: 0,
        deposit: 0,
        billableExpenseTransactionId: null,
    };
}

function lineToExtraction(
    line: NativeInvoiceLineRow,
): InvoiceExtractionLineItem {
    return {
        lineId: line.id,
        item: emptyToNull(line.productOrService),
        description: emptyToNull(line.description),
        amount: computeAccrualLineAmount({
            quantity: line.quantity,
            unitPrice: line.unitAmount,
            amount: 0,
        }),
        quantity: line.quantity,
        unitPrice: line.unitAmount,
        classIds: line.classIds.length > 0 ? line.classIds : null,
        accountId: line.accrualAccountId,
        accrualAccountId: line.receivablesAccountId,
        catalogItemId: line.catalogItemId,
        catalogItemPriceId: line.catalogItemPriceId,
    };
}

export function nativeInvoiceFieldsFromExtraction({
    extraction,
    customer,
}: {
    extraction: InvoiceExtractionRecord;
    customer: NativeInvoiceCustomer | null;
}): AccrualDraftInvoiceFormSlice {
    return {
        customer: customerFromExtraction(extraction, customer),
        invoiceNumber: extraction.documentNumber ?? "",
        invoiceDate:
            extraction.documentDate ??
            formatISO(new Date(), { representation: "date" }),
        dueDate: extraction.dueDate ?? "",
        note: extraction.memo ?? "",
        lines: extraction.lineItems.map(lineFromExtraction),
    };
}

export function applyInvoiceExtractionToRawState({
    raw,
    extraction,
    customer,
}: {
    raw: NativeInvoiceFormRawState;
    extraction: InvoiceExtractionRecord;
    customer: NativeInvoiceCustomer | null;
}): NativeInvoiceFormRawState {
    const fields = nativeInvoiceFieldsFromExtraction({ extraction, customer });
    return {
        ...raw,
        customer: fields.customer,
        invoiceNumberOverride: fields.invoiceNumber,
        invoiceDate: fields.invoiceDate,
        dueDate: fields.dueDate,
        note: fields.note,
        lines: fields.lines.length > 0 ? fields.lines : raw.lines,
        ...(extraction.taxAmount != null
            ? { tax: extraction.taxAmount, taxMode: "amount" as const }
            : {}),
        ...(extraction.discountAmount != null
            ? {
                  discount: extraction.discountAmount,
                  discountMode: "amount" as const,
              }
            : {}),
        ...(extraction.depositAmount != null
            ? {
                  deposit: extraction.depositAmount,
                  depositMode: "amount" as const,
              }
            : {}),
    };
}

export type AccrualDraftRequiredField =
    | "item"
    | "entity"
    | "customer"
    | "account"
    | "invoiceNumber"
    | "dueDate";

export function accrualDraftMissingFields({
    entityId,
    customerId,
    invoiceNumber,
    dueDate,
    lines,
}: {
    entityId: number | null;
    customerId: string;
    invoiceNumber: string;
    dueDate: string;
    lines: Array<{
        quantity: number | null;
        unitAmount: number | null;
        accrualAccountId: string | null;
    }>;
}): AccrualDraftRequiredField[] {
    const itemLines = lines.filter(
        (line) => multiplyNativeInvoiceLineAmount(line) !== 0,
    );
    const missing: AccrualDraftRequiredField[] = [];
    if (itemLines.length === 0) {
        missing.push("item");
    }
    if (entityId == null) {
        missing.push("entity");
    }
    if (customerId.trim() === "") {
        missing.push("customer");
    }
    if (
        itemLines.length === 0 ||
        itemLines.some((line) => line.accrualAccountId == null)
    ) {
        missing.push("account");
    }
    if (invoiceNumber.trim() === "") {
        missing.push("invoiceNumber");
    }
    if (dueDate.trim() === "") {
        missing.push("dueDate");
    }
    return missing;
}

export function blankInvoiceExtraction(): InvoiceExtractionRecord {
    return {
        id: uuidv7(),
        counterpartyName: null,
        counterpartyId: null,
        counterpartyIdSource: null,
        documentNumber: null,
        documentDate: null,
        dueDate: null,
        totalAmount: null,
        taxAmount: null,
        discountAmount: null,
        depositAmount: null,
        memo: null,
        balanceDue: null,
        paymentDate: null,
        entityId: null,
        warnings: null,
        lineItems: [],
    };
}

export function invoiceExtractionFromFormState({
    extraction,
    state,
    totalAmount,
    taxAmount,
    discountAmount,
    depositAmount,
    entityId,
}: {
    extraction: InvoiceExtractionRecord;
    state: AccrualDraftInvoiceFormSlice;
    totalAmount: number;
    taxAmount: number;
    discountAmount: number;
    depositAmount: number;
    entityId: number | null;
}): InvoiceExtractionRecord {
    const counterpartyId = emptyToNull(state.customer?.id ?? "");
    const counterpartyName = counterpartyId
        ? emptyToNull(state.customer?.name ?? "")
        : null;
    const counterpartyIdSource =
        counterpartyId === (extraction.counterpartyId ?? null)
            ? (extraction.counterpartyIdSource ?? null)
            : FIELD_UPDATE_SOURCE.ACTIONS;

    return {
        ...extraction,
        counterpartyId,
        counterpartyName,
        counterpartyIdSource,
        documentNumber: emptyToNull(state.invoiceNumber),
        documentDate: emptyToNull(state.invoiceDate),
        dueDate: emptyToNull(state.dueDate),
        memo: emptyToNull(state.note),
        totalAmount:
            extraction.totalAmount ?? roundToCents(totalAmount + depositAmount),
        taxAmount,
        discountAmount,
        depositAmount,
        entityId,
        lineItems: state.lines.map(lineToExtraction),
    };
}
